
import React, { useState, useEffect, useCallback } from 'react';
import { User } from '../types';

interface PiiAccessTabProps {
  users: User[];
}

const API_BASE = (import.meta.env.VITE_API_URL as string) || '/api/index.php';

const PII_ACTIONS = ['view_pii_leads', 'view_pii_users', 'gdpr_export', 'gdpr_erase', 'support_cross_company_access'];

const piiActionLabels: Record<string, { label: string; dark: string; dot: string }> = {
  'view_pii_leads':               { label: 'Consultation prospects',    dark: 'bg-violet-500/20 text-violet-300 border border-violet-500/30',  dot: 'bg-violet-400' },
  'view_pii_users':               { label: 'Consultation utilisateurs', dark: 'bg-purple-500/20 text-purple-300 border border-purple-500/30',  dot: 'bg-purple-400' },
  'gdpr_export':                  { label: 'Export RGPD (portabilité)', dark: 'bg-sky-500/20    text-sky-300    border border-sky-500/30',     dot: 'bg-sky-400'    },
  'gdpr_erase':                   { label: 'Effacement RGPD',           dark: 'bg-rose-500/20   text-rose-300   border border-rose-500/30',    dot: 'bg-rose-400'   },
  'support_cross_company_access': { label: 'Accès inter-entreprise',    dark: 'bg-amber-500/20  text-amber-300  border border-amber-500/30',   dot: 'bg-amber-400'  },
};

const PiiAccessTab: React.FC<PiiAccessTabProps> = ({ users }) => {
  const [logs, setLogs] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(0);
  const [loading, setLoading] = useState(false);
  const [filterAction, setFilterAction] = useState('');
  const [filterUser, setFilterUser] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const fetchLogs = useCallback(async (p = 1) => {
    setLoading(true);
    try {
      const token = sessionStorage.getItem('orion_access_token') || localStorage.getItem('auth_token');
      const params = new URLSearchParams({
        action: 'get_audit_logs',
        page: String(p),
        limit: '30',
        filter_action: filterAction || PII_ACTIONS.join('|'),
      });
      if (filterUser) params.set('filter_user', filterUser);
      if (dateFrom) params.set('date_from', dateFrom);
      if (dateTo) params.set('date_to', dateTo);
      const res = await fetch(`${API_BASE}?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      const data = await res.json();
      const piilogs = (data.logs || []).filter((l: any) => PII_ACTIONS.includes(l.action));
      setLogs(piilogs);
      setTotal(data.total || 0);
      setPage(data.page || 1);
      setPages(data.pages || 0);
    } catch {
      setLogs([]);
    }
    setLoading(false);
  }, [filterAction, filterUser, dateFrom, dateTo]);

  useEffect(() => { fetchLogs(1); }, [fetchLogs]);

  return (
    <div className="p-12 animate-in fade-in">

      {/* En-tête */}
      <h3 className="text-2xl font-extrabold text-slate-900 mb-1 tracking-tight">
        Audit des accès aux données personnelles
      </h3>
      <p className="text-slate-500 text-sm font-medium mb-3">
        Conformément au RGPD, chaque accès en lecture aux données personnelles (PII) est tracé ici.
      </p>

      {/* Badges RGPD */}
      <div className="flex items-center gap-2 mb-8">
        <span className="text-[10px] font-black uppercase tracking-widest bg-violet-500/20 text-violet-300 border border-violet-500/30 px-2.5 py-1 rounded-lg">
          RGPD Art. 5(2)
        </span>
        <span className="text-[10px] font-black uppercase tracking-widest bg-emerald-500/20 text-emerald-300 border border-emerald-500/30 px-2.5 py-1 rounded-lg">
          Responsabilité
        </span>
      </div>

      {/* Filtres rapides par type */}
      <div className="flex flex-wrap gap-2 mb-6">
        {Object.entries(piiActionLabels).map(([key, { label, dark }]) => (
          <button
            key={key}
            type="button"
            onClick={() => setFilterAction(filterAction === key ? '' : key)}
            className={`text-[10px] font-bold px-3 py-1.5 rounded-lg transition-all ${dark} ${
              filterAction === key ? 'ring-2 ring-offset-1 ring-offset-transparent ring-white/20 opacity-100' : 'opacity-60 hover:opacity-100'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {/* Filtres */}
      <div className="flex flex-wrap gap-3 mb-6 p-4 rounded-2xl border orion-settings-pref-card">
        <div className="flex flex-col gap-1">
          <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Utilisateur</label>
          <select
            title="Filtrer par utilisateur"
            value={filterUser}
            onChange={e => setFilterUser(e.target.value)}
            className="border rounded-lg px-3 py-2 text-xs font-bold outline-none focus:ring-2 focus:ring-violet-500 min-w-[150px]"
          >
            <option value="">Tous les utilisateurs</option>
            {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
          </select>
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Du</label>
          <input
            type="date"
            title="Date début"
            value={dateFrom}
            onChange={e => setDateFrom(e.target.value)}
            className="border rounded-lg px-3 py-2 text-xs font-bold outline-none focus:ring-2 focus:ring-violet-500"
          />
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Au</label>
          <input
            type="date"
            title="Date fin"
            value={dateTo}
            onChange={e => setDateTo(e.target.value)}
            className="border rounded-lg px-3 py-2 text-xs font-bold outline-none focus:ring-2 focus:ring-violet-500"
          />
        </div>
        <div className="flex items-end">
          <button
            type="button"
            onClick={() => { setFilterAction(''); setFilterUser(''); setDateFrom(''); setDateTo(''); }}
            className="text-[10px] font-black text-slate-400 uppercase tracking-widest hover:text-slate-200 px-3 py-2 transition-colors"
          >
            Réinitialiser
          </button>
        </div>
      </div>

      {/* Résumé */}
      <div className="flex items-center gap-3 mb-4">
        <span className="text-xs font-bold text-slate-400">
          {total} entrée{total > 1 ? 's' : ''} totale{total > 1 ? 's' : ''}
        </span>
        {loading && <span className="text-xs text-slate-400 animate-pulse">Chargement...</span>}
      </div>

      {/* Tableau */}
      <div className="overflow-x-auto rounded-2xl border orion-settings-table-shell">
        <table className="w-full text-left">
          <thead className="text-[10px] uppercase font-bold tracking-widest orion-settings-table-head text-slate-400">
            <tr>
              <th className="px-6 py-4">Date & Heure</th>
              <th className="px-6 py-4">Utilisateur</th>
              <th className="px-6 py-4">Type d'accès</th>
              <th className="px-6 py-4">Périmètre</th>
              <th className="px-6 py-4">Détails</th>
              <th className="px-6 py-4">IP</th>
            </tr>
          </thead>
          <tbody className="divide-y orion-settings-table-body">
            {logs.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-6 py-8 text-center text-xs text-slate-400 italic">
                  {loading ? 'Chargement...' : 'Aucun accès aux données personnelles enregistré.'}
                </td>
              </tr>
            ) : logs.map((log: any) => {
              const info = piiActionLabels[log.action] || { label: log.action, dark: 'bg-slate-500/20 text-slate-300 border border-slate-500/30', dot: 'bg-slate-400' };
              return (
                <tr key={log.id} className="orion-settings-table-row text-sm">
                  <td className="px-6 py-3 text-xs font-mono text-slate-500 whitespace-nowrap">
                    {new Date(log.created_at).toLocaleString('fr-FR', {
                      day: '2-digit', month: '2-digit', year: 'numeric',
                      hour: '2-digit', minute: '2-digit', second: '2-digit',
                    })}
                  </td>
                  <td className="px-6 py-3">
                    <div className="flex items-center gap-2">
                      <div className="w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-black text-white orion-settings-avatar">
                        {(log.user_name || '?')[0].toUpperCase()}
                      </div>
                      <div>
                        <div className="text-xs font-bold text-slate-900">{log.user_name || 'Système'}</div>
                        <div className="text-[10px] text-slate-400">{log.user_role || ''}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-3">
                    <span className={`text-[10px] font-bold uppercase px-2.5 py-1 rounded-lg ${info.dark}`}>
                      {info.label}
                    </span>
                  </td>
                  <td className="px-6 py-3 text-xs text-slate-500 font-mono">
                    {log.target_type && log.target_id ? `${log.target_type} #${log.target_id}` : '—'}
                  </td>
                  <td className="px-6 py-3 text-xs text-slate-500 max-w-[260px] truncate" title={log.details || ''}>
                    {log.details || '—'}
                  </td>
                  <td className="px-6 py-3 text-[10px] font-mono text-slate-400">
                    {log.ip_address || '—'}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {pages > 1 && (
        <div className="flex items-center justify-center gap-2 mt-6">
          <button
            type="button"
            disabled={page <= 1}
            onClick={() => fetchLogs(page - 1)}
            className="px-3 py-1.5 rounded-lg text-xs font-bold text-slate-400 hover:text-slate-200 hover:bg-white/5 disabled:opacity-30 transition-colors"
          >
            Précédent
          </button>
          <span className="text-xs font-bold text-slate-400">Page {page} / {pages}</span>
          <button
            type="button"
            disabled={page >= pages}
            onClick={() => fetchLogs(page + 1)}
            className="px-3 py-1.5 rounded-lg text-xs font-bold text-slate-400 hover:text-slate-200 hover:bg-white/5 disabled:opacity-30 transition-colors"
          >
            Suivant
          </button>
        </div>
      )}

      {/* Note RGPD */}
      <div className="mt-8 p-4 rounded-2xl bg-violet-500/10 border border-violet-500/20">
        <p className="text-[11px] text-violet-300 font-semibold leading-relaxed">
          Ces journaux sont conservés 12 mois conformément à la politique de rétention RGPD.
          Chaque entrée enregistre l'identité de l'utilisateur, l'horodatage, l'adresse IP et le périmètre des données accédées.
        </p>
      </div>

    </div>
  );
};

export default PiiAccessTab;
